import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { LogIn, MapPin, AlertCircle, Mail } from 'lucide-react';
import { agentAuthService } from '../../services/agentAuthService';
import { trackAuth, trackError } from '../../services/analyticsService';

/**
 * Agent Abidjan Login Page
 * Dedicated authentication for ADT Express agents in Abidjan
 * Mobile-first design optimized for field operations
 */
export default function AgentAbidjanLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await agentAuthService?.signIn(email, password);
      
      if (result?.success) {
        // Track successful agent login (skip in sandboxed environments)
        try {
          const isSandboxed = window.location?.hostname?.includes('builtwithrocket.new') || 
                              window.location?.hostname?.includes('rocket.new');
          if (!isSandboxed && typeof window.gtag !== 'undefined') {
            trackAuth('login', 'agent_abidjan');
          }
        } catch (analyticsError) {
          console.warn('Analytics tracking skipped:', analyticsError);
        }
        navigate('/dashboard-agent-abidjan', { replace: true });
      } else {
        trackError('auth_error', result?.error || 'Login failed', { user_type: 'agent_abidjan' });
        setError(result?.error || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Erreur de connexion');
      trackError('auth_error', err?.message, { user_type: 'agent_abidjan' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Connexion Agent Abidjan - ADT Express</title>
        <meta name="description" content="Portail de connexion sécurisé pour les agents de livraison ADT Express à Abidjan" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-green-100">
          <div className="max-w-md mx-auto px-4 py-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="bg-green-600 rounded-lg p-2">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <h1 className="text-xl font-bold text-gray-900">ADT Express</h1>
                <p className="text-sm text-green-600 font-medium">Agent Abidjan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-8 text-center">
                <div className="bg-white/20 rounded-full p-4 inline-block mb-4">
                  <LogIn className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Portail Agent</h2>
                <p className="text-green-100 text-sm">Connectez-vous pour accéder à vos tâches</p>
              </div>

              {/* Login Form */}
              <div className="px-6 py-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Error Alert */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e?.target?.value)}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                        placeholder="agent@adtexpress.fr"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                        Mot de passe
                      </label>
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                      placeholder="••••••••"
                    />
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      Rester connecté
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Connexion...</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Se connecter</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Demo Credentials */}
                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-green-800 mb-2 uppercase tracking-wide">Comptes de démonstration :</p>
                  <div className="space-y-2 text-sm">
                    <div className="bg-white rounded p-2 border border-green-100">
                      <p className="font-medium text-gray-900">Agent 1 :</p>
                      <p className="text-gray-600">agent.abj1@adtexpress.ci</p>
                      <p className="text-gray-600">Mot de passe : agent123</p>
                    </div>
                    <div className="bg-white rounded p-2 border border-green-100">
                      <p className="font-medium text-gray-900">Agent 2 :</p>
                      <p className="text-gray-600">agent.abj2@adtexpress.ci</p>
                      <p className="text-gray-600">Mot de passe : agent123</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Support technique :</strong> En cas de problème de connexion, contactez l'administration ADT Express.
                </p>
              </div>
              <p className="text-xs text-gray-500">
                © 2025 ADT Express. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}